import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import numeral from 'numeral'

import { Iconify, Modal } from '../../../../Xmerald-UI'
import Loading from '../../../../components/Loading'
import { useCasinoRounds, RANGE_OPTIONS } from '../../hooks/use-casino-rounds'
import { useCasinoTransactions } from '../../hooks/use-casino-transactions'

import {
  EmptyState,
  ExpandIcon,
  FilterLabel,
  FilterRow,
  GameThumb,
  LoadingMore,
  ModalBody,
  RangeSelect,
  RangeSelectIcon,
  RangeSelectWrapper,
  RoundCard,
  RoundDate,
  RoundHeader,
  RoundId,
  RoundInfo,
  RoundMeta,
  RoundStats,
  Sentinel,
  StatValue,
  TransactionAmount,
  TransactionRow,
  TransactionsPanel,
  RoundsList,
} from './history-modal.styled'

const OPERATION_LABELS = {
  debit: 'Débito',
  credit: 'Crédito',
  cancel: 'Cancelación',
  end: 'Finalizar',
  debit_credit: 'Débito/Crédito',
}

const getResultVariant = (amount) => {
  if (amount > 0) return 'win'
  if (amount < 0) return 'lose'
  return 'neutral'
}

const formatCurrency = (value) => `${numeral(value).format('$0,000.00')} mxn`

const RoundCardItem = ({ casinoRound }) => {
  const [expanded, setExpanded] = useState(false)
  const variant = getResultVariant(casinoRound.total_amount)
  const { transactions, isLoading } = useCasinoTransactions(
    casinoRound.pariplay_token_id,
    expanded
  )

  return (
    <RoundCard $variant={variant}>
      <RoundHeader type="button" onClick={() => setExpanded((prev) => !prev)}>
        <GameThumb src={casinoRound.game_thumbnail} alt="" loading="lazy" />
        <RoundInfo>
          <RoundMeta>
            <RoundId>#{casinoRound.pariplay_token_id}</RoundId>
            <RoundDate>{moment(casinoRound.created).format('DD MMM YYYY HH:mm')}</RoundDate>
          </RoundMeta>
          <RoundStats>
            <span>
              Arriesga: <StatValue $variant="lose">{formatCurrency(casinoRound.total_bet)}</StatValue>
            </span>
            <span>
              Gana: <StatValue $variant="win">{formatCurrency(casinoRound.total_win)}</StatValue>
            </span>
            <span>
              Resultado:{' '}
              <StatValue $variant={variant}>{formatCurrency(casinoRound.total_amount)}</StatValue>
            </span>
          </RoundStats>
        </RoundInfo>
        <ExpandIcon $expanded={expanded} aria-hidden="true">
          <Iconify icon="eva:chevron-down-fill" width={20} height={20} />
        </ExpandIcon>
      </RoundHeader>

      {expanded ? (
        <TransactionsPanel>
          {isLoading ? (
            <LoadingMore>
              <Iconify icon="svg-spinners:ring-resize" width={22} height={22} />
            </LoadingMore>
          ) : transactions.length === 0 ? (
            <EmptyState>Sin transacciones registradas</EmptyState>
          ) : (
            transactions.map((transaction) => {
              const txVariant = getResultVariant(transaction.amount)

              return (
                <TransactionRow key={transaction.id}>
                  <span>
                    {transaction.id} · {moment(transaction.created).format('DD MMM YYYY HH:mm:ss')}
                  </span>
                  <span>{OPERATION_LABELS[transaction.operation] ?? transaction.operation}</span>
                  <TransactionAmount $variant={txVariant}>
                    {formatCurrency(transaction.amount)}
                  </TransactionAmount>
                </TransactionRow>
              )
            })
          )}
        </TransactionsPanel>
      ) : null}
    </RoundCard>
  )
}

export const HistoryModal = ({ show, onClose }) => {
  const [range, setRange] = useState('all')
  const sentinelRef = useRef(null)
  const listRef = useRef(null)

  const {
    rounds,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCasinoRounds(range)

  const handleRangeChange = (event) => {
    setRange(event.target.value)
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (!show) return undefined

    const sentinel = sentinelRef.current
    if (!sentinel) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore()
        }
      },
      { root: listRef.current, rootMargin: '120px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [show, loadMore, rounds.length])

  return (
    <Modal show={show} title="Historial" onClose={onClose}>
      <ModalBody>
        <FilterRow>
          <FilterLabel>Periodo</FilterLabel>
          <RangeSelectWrapper>
            <RangeSelect
              id="history-range"
              value={range}
              onChange={handleRangeChange}
              aria-label="Filtrar por periodo"
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </RangeSelect>
            <RangeSelectIcon aria-hidden="true">
              <Iconify icon="eva:chevron-down-fill" width={20} height={20} />
            </RangeSelectIcon>
          </RangeSelectWrapper>
        </FilterRow>

        {isLoading ? (
          <Loading />
        ) : rounds.length === 0 ? (
          <EmptyState>
            <Iconify icon="eva:clock-outline" width={36} height={36} />
            <span>No hay jugadas en este periodo</span>
          </EmptyState>
        ) : (
          <RoundsList ref={listRef}>
            {rounds.map((casinoRound) => (
              <RoundCardItem
                key={casinoRound.pariplay_token_id}
                casinoRound={casinoRound}
              />
            ))}
            <Sentinel ref={sentinelRef} />
            {isFetchingNextPage ? (
              <LoadingMore>
                <Iconify icon="svg-spinners:ring-resize" width={22} height={22} />
              </LoadingMore>
            ) : null}
          </RoundsList>
        )}
      </ModalBody>
    </Modal>
  )
}
